import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/lib/i18n";

const CONSENT_KEY = "stillpoint-ga-consent";

const COPY = {
  el: {
    title: "Ανώνυμα στατιστικά",
    text: "Χρησιμοποιώ μόνο ανώνυμα cookies για να βλέπω πώς λειτουργεί η ιστοσελίδα — χωρίς διαφημίσεις, χωρίς παρακολούθηση προσωπικών δεδομένων.",
    accept: "Αποδοχή",
    reject: "Απόρριψη",
  },
  en: {
    title: "Anonymous statistics",
    text: "I only use anonymous cookies to see how the website is used — no ads, no tracking of personal data.",
    accept: "Accept",
    reject: "Decline",
  },
};

const ConsentBanner = () => {
  const { lang } = useLang();
  const [visible, setVisible] = useState(false);
  const c = COPY[lang];

  useEffect(() => {
    let stored = null;
    try {
      stored = localStorage.getItem(CONSENT_KEY);
    } catch (e) {
      stored = null;
    }
    if (stored === "granted") {
      if (typeof window.enableAnalytics === "function") window.enableAnalytics();
    } else if (stored === null) {
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const decide = (choice) => {
    try {
      localStorage.setItem(CONSENT_KEY, choice);
    } catch (e) {
      /* storage unavailable — session-only choice */
    }
    setVisible(false);
    if (choice === "granted" && typeof window.enableAnalytics === "function") {
      window.enableAnalytics();
      if (typeof window.gtag === "function") {
        window.gtag("event", "page_view", {
          page_path: window.location.pathname,
          page_title: document.title,
        });
      }
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="fixed bottom-4 inset-x-4 z-[60] mx-auto max-w-md rounded-2xl bg-card p-5 shadow-xl ring-1 ring-sage-600/10"
          data-testid="consent-banner"
          role="dialog"
          aria-live="polite"
        >
          <p className="font-display text-lg text-sage-800">{c.title}</p>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{c.text}</p>
          <div className="mt-4 flex gap-3">
            <button
              onClick={() => decide("granted")}
              data-testid="consent-accept"
              className="flex-1 rounded-full bg-sage-600 px-4 py-2.5 text-sm font-medium text-background transition-colors duration-200 hover:bg-sage-700"
            >
              {c.accept}
            </button>
            <button
              onClick={() => decide("denied")}
              data-testid="consent-reject"
              className="flex-1 rounded-full px-4 py-2.5 text-sm font-medium text-sage-700 ring-1 ring-sage-600/30 transition-colors duration-200 hover:bg-sage-600/5"
            >
              {c.reject}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConsentBanner;
